/* eslint-disable @typescript-eslint/no-explicit-any */
import useAlertService from "@mirage/hooks/useAlertService";
import { DateFieldPopper, EntityTableProvider } from "@mirage/store/entityTableStore";
import { TablePaginationBaseProps } from "@mui/material/TablePagination";
import {
    DataGridPremium,
    DataGridPremiumProps,
    GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    GridColDef,
    GridColumnHeaderParams,
    GridColumnResizeParams,
    GridColumnVisibilityModel,
    GridEventListener,
    GridFilterModel,
    GridPinnedColumns,
    GridRenderCellParams,
    GridRowClassNameParams,
    GridRowId,
    GridRowModesModel,
    GridRowParams,
    GridSortItem,
    GridSortModel,
    LicenseInfo,
    MuiEvent,
    useGridApiRef,
} from "@mui/x-data-grid-premium";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import { Filter } from "@shared/mirage/entityTable/Filter";
import { EntityTableHelper } from "@shared/mirage/helpers/EntityTableHelper";
import { EntityBinding, Relations } from "@shared/mirage/metadata/BaseEntityRecordBinding";
import _, { cloneDeep, isEqual } from "lodash";
import { ReactNode, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomNoRowsOverlay from "./CustomNoRowsOverlay";
import { EntityHelper } from "./EntityTableHelper";
import { EntityTableStateStorage } from "./EntityTableStateStorage";
import ColumnMenu from "./components/ColumnMenu";
import CustomGridToolbar, { CustomGridToolbarOptions } from "./components/CustomGridToolbar";
import DataGridActionsColumn, { DataGridActionsColumnProps } from "./components/DataGridActionsColumn";
import Footer, { GridLegendItem } from "./components/Footer";
import DateFilterModal from "./components/GridDatesFilter/DateFilterModal";
import { MetadataRegistry } from "@shared/mirage/metadata/MetaBinding";
import { UserPreferencesService } from "@mirage/services/UserPreferencesService";
import { IUserTablePreferences } from "@shared/mirage/services/IUserPreferencesService";
import { useRecoilState } from "recoil";
import { userPreferencesState } from "@mirage/store/UserStore";
import { useTheme } from "@mui/material";

const userPreferencesService = new UserPreferencesService();

LicenseInfo.setLicenseKey("b9ac4f111a52fb43bf73d2f7a6be9b1cTz03OTk5NixFPTE3MzMzOTE4MDMwMDAsUz1wcmVtaXVtLExNPXN1YnNjcmlwdGlvbixLVj0y");

export interface EntityTableProps
    extends Omit<
        DataGridPremiumProps,
        | "rows"
        | "rowCount"
        | "loading"
        | "paginationMode"
        | "onPaginationModelChange"
        | "paginationModel"
        | "filterMode"
        | "onFilterModelChange"
        | "filterModel"
        | "sortingMode"
        | "onSortModelChange"
        | "disableRowGrouping"
        | "initialState"
        | "autoHeight"
        | "hideFooter"
        | "onPinnedColumnsChange"
    > {
    onPageRequest: (page: IEntityPageRequest) => Promise<any>;
    key: string;
    stateKey: string;
    entityName?: string; // should be required - to do
    title?: string;
    noRecordsImage?: boolean;
    noRecordsMessage?: string;
    filter?: Filter;
    onRecordSelected?: (record: any) => void;
    relations?: Relations;
    forceFetch?: boolean;
    toolbarOptions?: CustomGridToolbarOptions;
    actionsColumn?: Omit<DataGridActionsColumnProps, "setTableState" | "gridRef">;
    customTogglePanelCol?: ICustomTogglePanelCol;
    onEditRecordSave?: (id: GridRowId) => Promise<void>;
    excludeBaseColumns?: boolean;
    disableFilterOnBaseColumns?: boolean;
    showAll?: boolean;
    /**
     * @deprecated use entityName instead
     */
    metadata?: EntityBinding<unknown>;
    hideEmpty?: boolean;
    hideRowsPerPage?: boolean;
    hideToolbar?: boolean;
    showQuickSearch?: boolean;
    legend?: GridLegendItem[];
    gridHeight?: string;
    gridMaxHeight?: string;
    pageSize?: number;
    /**
     * @default "true"
     */
    pagination?: boolean;
    /**
     * Columns visibility model affects data being fetched from the server, this flag can override this behavior
     * @default "false"
     */
    fetchAllColumns?: boolean;
    customColumnsOrder?: { [key: string]: string };
}

export interface ITableState {
    rows: any[];
    total: number;
    pageIndex: number;
    pageSize: number;
    sortModel: GridSortItem[];
    customFilter: Filter | null;
    filterModel: GridFilterModel;
    isLoading: boolean;
    rowModesModel: GridRowModesModel;
    selectedRow: { id: GridRowId } | null;
    columnVisibilityModel?: GridColumnVisibilityModel;
    pinnedColumns?: GridPinnedColumns;
}

const EntityTable = memo((props: EntityTableProps) => {
    const { stateKey, actionsColumn, hideEmpty, legend, entityName, fetchAllColumns = false, customColumnsOrder } = props;
    const metadata = entityName ? MetadataRegistry.get(entityName) : props.metadata;
    const relations = metadata?.relations || props.relations || {};
    const showAlert = useAlertService();
    const currentTheme = useTheme();
    const isPublicOnlyMode = !!currentTheme.entityTable?.publicOnly;
    const excludeBaseColumns = props.excludeBaseColumns || !!isPublicOnlyMode;
    const [userPreferences, setUserPreferences] = useRecoilState(userPreferencesState);
    const initialRequest = useMemo(() => EntityTableStateStorage.getState(stateKey), [stateKey]);
    const formattedSortModel = useMemo(() => EntityHelper.createSortModel(initialRequest?.order, props.sortModel), [initialRequest?.order, props.sortModel]);
    const columnVisibilityModel = useMemo(
        () => EntityHelper.prepareColumnVisibilityModel(props.columns, props.columnVisibilityModel, excludeBaseColumns, formattedSortModel),
        [excludeBaseColumns, formattedSortModel, props.columnVisibilityModel, props.columns]
    );
    const [tableState, setTableState] = useState({
        rows: [],
        total: 0,
        pageIndex: EntityHelper.getPageIndex(initialRequest),
        pageSize: props.pageSize || 10,
        isLoading: false,
        sortModel: formattedSortModel,
        customFilter: initialRequest?.filterModel?.items.length ? EntityHelper.prepareFilterObject(initialRequest?.filterModel) : null,
        filterModel: initialRequest?.filterModel || ({ items: [] } as GridFilterModel),
        rowModesModel: {} as GridRowModesModel,
        selectedRow: null,
        columnVisibilityModel: columnVisibilityModel,
        pinnedColumns: props.pinnedColumns || ({} as GridPinnedColumns),
    } as ITableState);
    const isOutOfScope = tableState.pageIndex > 0 && !tableState.rows.length;
    const [dateFilterPopper, setDateFilterPopper] = useState<DateFieldPopper>({ open: false, elementRef: null, fieldName: "" });
    const uiSettings = useRef<IUserTablePreferences | null>(null);
    const apiRef = useGridApiRef();
    const gridApiRef = props.apiRef || apiRef;
    const [panelAnchorEl, setPanelAnchorEl] = useState<HTMLElement | null>(null);
    EntityHelper.setLookupTypeForRelationColumns(props.columns, relations);
    EntityHelper.setRequiredColumns(props.columns, metadata?.columns);
    const setActionsColumn = useCallback(
        (columns: GridColDef<any, any, any>[]) => {
            if (!!actionsColumn?.actions || !!actionsColumn?.deleteAction || !!actionsColumn?.editAction) {
                columns.unshift(DataGridActionsColumn({ setTableState, gridRef: gridApiRef, ...actionsColumn }));
            }
        },
        [actionsColumn, gridApiRef]
    );
    const setPanelToggleColumn = useCallback(
        (columns: GridColDef<any, any, any>[]) => {
            if (props.customTogglePanelCol) {
                const { renderCell, headerName } = props.customTogglePanelCol;
                const togglePanelColumn = {
                    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
                    headerName,
                    renderCell,
                } as GridColDef<any, any, any>;
                columns.unshift(togglePanelColumn);
            }
        },
        [props.customTogglePanelCol]
    );

    const applyFilterOnHeader = useCallback(
        (columns: GridColDef[]) => {
            if (!gridApiRef.current) {
                return;
            }
            if (isPublicOnlyMode) {
                return columns;
            }
            columns.forEach((column) => {
                column.renderHeader = (params: GridColumnHeaderParams) => {
                    const filter = tableState.filterModel.items.find((item) => item.field === column.field);
                    const originalValue: string | string[] | undefined = filter?.value;
                    let filterValue = Array.isArray(originalValue) ? originalValue.join(", ") : originalValue;
                    if (!filterValue && filter?.operator) {
                        filterValue = filter.operator === "isEmpty" ? "(is empty)" : filter.operator === "isNotEmpty" ? "(is not empty)" : "";
                    }
                    const element = (
                        <span>
                            <strong
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const elementRef = gridApiRef.current.getColumnHeaderElement(column.field);
                                    setPanelAnchorEl(elementRef);
                                    if (column.type === "date" || column.type === "datetime") {
                                        gridApiRef.current.hideFilterPanel();
                                        setDateFilterPopper({ open: true, elementRef, fieldName: column.field });
                                    } else {
                                        gridApiRef.current.showFilterPanel(params.field);
                                    }
                                }}
                            >
                                {params.colDef.headerName}
                            </strong>
                            <div
                                style={{
                                    fontSize: "0.75rem",
                                    lineHeight: "0.75rem",
                                    position: "absolute",
                                    bottom: "4px",
                                    fontWeight: 500,
                                    color: "rgb(66, 165, 245)",
                                    // textTransform: "uppercase",
                                }}
                            >
                                {filterValue}
                            </div>
                        </span>
                    );

                    return element;
                };
            });
        },
        [gridApiRef, tableState.filterModel.items]
    );

    const columns = EntityHelper.formatColumns(
        cloneDeep(props.columns),
        excludeBaseColumns,
        setActionsColumn,
        setPanelToggleColumn,
        props.disableFilterOnBaseColumns,
        metadata?.columns,
        formattedSortModel,
        applyFilterOnHeader,
        uiSettings.current
    );

    const rowMode = useMemo(() => {
        if (!tableState.selectedRow) {
            return "view";
        }
        const { id } = tableState.selectedRow;
        return tableState.rowModesModel[id]?.mode || "view";
    }, [tableState.rowModesModel, tableState.selectedRow]);

    useEffect(() => {
        const copy = { ...uiSettings.current };
        uiSettings.current = {
            ...copy,
            pageSize: tableState.pageSize,
            columnVisibilityModel: tableState.columnVisibilityModel,
            pinnedColumns: tableState.pinnedColumns,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableState.columnVisibilityModel, tableState.pageSize, tableState.pinnedColumns]);

    useEffect(() => {
        setTableState((p) => {
            return { ...p, pageIndex: 0 };
        });
    }, [isOutOfScope]);

    useEffect(() => {
        if (!stateKey) {
            return;
        }
        const fetchSettings = () => {
            const settings = userPreferences?.entityTables?.[stateKey];
            if (settings) {
                uiSettings.current = settings;
                setTableState((p) => {
                    return {
                        ...p,
                        pageSize: settings.pageSize || p.pageSize,
                        columnVisibilityModel: settings.columnVisibilityModel ? settings.columnVisibilityModel : p.columnVisibilityModel,
                        pinnedColumns: settings.pinnedColumns ? settings.pinnedColumns : p.pinnedColumns,
                    };
                });
            }
        };
        fetchSettings();
    }, [stateKey, userPreferences]);

    useEffect(() => {
        return () => {
            if (!stateKey) {
                return;
            }
            const ui = uiSettings.current;
            if (!ui) {
                return;
            }
            const visibilityModelChanged = !isEqual(ui.columnVisibilityModel, tableState.columnVisibilityModel);
            const pinnedColumnsChanged = !isEqual(ui.pinnedColumns, tableState.pinnedColumns);
            const pageSizeChanged = ui.pageSize !== tableState.pageSize;
            const columnOrderChanged =
                ui.columnOrder &&
                !isEqual(
                    ui.columnOrder,
                    props.columns.map((el) => el.field)
                );

            if (!ui || (!ui.columns?.length && !visibilityModelChanged && !pinnedColumnsChanged && !pageSizeChanged && !columnOrderChanged)) {
                return;
            }
            setUserPreferences((prev) => {
                return {
                    ...prev,
                    entityTables: {
                        ...prev.entityTables,
                        [stateKey]: ui,
                    },
                };
            });
            userPreferencesService.savePreferences({ preference: "entityTables", key: stateKey, value: ui });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onRecordsFetch = async () => {
            const filter = EntityTableHelper.MergeFilters(props.filter, tableState.customFilter);
            setTableState((prevState) => ({ ...prevState, isLoading: true }));
            let pageRequest = EntityHelper.preparePageRequest(tableState, filter, columns, fetchAllColumns, customColumnsOrder);
            const existingState = { ...EntityTableStateStorage.getState(props.stateKey) };
            delete existingState.filterModel;
            pageRequest = _.assign(existingState, pageRequest);
            pageRequest.showAll = props.showAll;
            EntityTableStateStorage.setState(props.stateKey, { ...pageRequest, filterModel: tableState.filterModel });
            const response = await props.onPageRequest(pageRequest);
            if (response?.records && response.total >= 0) {
                setTableState((prevState) => ({ ...prevState, rows: response.records, total: response.total }));
            } else {
                if (response?.errorMsg) {
                    showAlert(response.errorMsg, "error");
                }
            }
            setTableState((prevState) => ({ ...prevState, isLoading: false }));
        };

        onRecordsFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        tableState.pageIndex,
        tableState.pageSize,
        tableState.sortModel,
        tableState.filterModel,
        props.forceFetch,
        props.filter,
        props.showAll,
        props.onPageRequest,
        tableState.columnVisibilityModel,
        fetchAllColumns,
    ]);

    const handleOnPageChange = (event: React.MouseEvent<HTMLButtonElement> | null, pageIndex: number) => {
        setTableState((prevState) => {
            const rowsWithoutNewRecords = prevState.rows.filter((row) => !row.isNew);
            return {
                ...prevState,
                rows: rowsWithoutNewRecords,
                pageIndex: pageIndex,
            };
        });
    };

    const handleOnPageSizeChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const newPageSize = Number(event.target.value);
        setTableState((prevState) => {
            const firstRecord = prevState.pageIndex * prevState.pageSize;
            return { ...prevState, pageSize: newPageSize, pageIndex: Math.floor(firstRecord / newPageSize) };
        });
    };

    const handleOnSortModelChange = (sortModel: GridSortModel = []) => {
        setTableState((prevState) => ({ ...prevState, sortModel: sortModel }));
    };
    const handleRowClick: GridEventListener<"rowClick"> = (params, event) => {
        const state = gridApiRef.current.state;
        if (_.isEmpty(state.editRows)) {
            event.preventDefault();
            event.stopPropagation();
            if (typeof props.onRecordSelected === "function") {
                props.onRecordSelected(params.row);
            }
        }
    };
    const handleCellClick: GridEventListener<"cellClick"> = (params, event) => {
        if (event.ctrlKey) {
            event.stopPropagation();
        }
    };
    const handleFilterModelChanged = useCallback(
        (filterModel: GridFilterModel) => {
            const columns = props.showQuickSearch ? props.columns : undefined;
            const filter = EntityHelper.prepareFilterObject(filterModel, columns);
            setTableState((prevState) => {
                return {
                    ...prevState,
                    pageIndex: 0,
                    customFilter: filter,
                    filterModel: filterModel,
                };
            });
        },
        [props.columns, props.showQuickSearch]
    );

    const handleRowsModeChanged = (newModel: GridRowModesModel) => {
        setTableState((prevState) => ({ ...prevState, rowModesModel: newModel }));
    };

    const handleRowEditStart = (params: GridRowParams, event: MuiEvent<React.SyntheticEvent>) => {
        event.defaultMuiPrevented = true;
    };
    const handleRowEditStop: GridEventListener<"rowEditStop"> = (params, event) => {
        event.defaultMuiPrevented = true;
    };
    const handleRowEditCommit: GridEventListener<"rowEditCommit"> = (params, event) => {
        event.defaultMuiPrevented = true;
    };
    const handleRowFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
        const row = event.currentTarget;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const id = row!.dataset.id!;
        setTableState((prev) => ({ ...prev, selectedRow: { id } }));
    }, []);

    const onColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
        setTableState((prev) => ({
            ...prev,
            columnVisibilityModel: model,
        }));
    };

    const paginationProps = {
        rowsPerPageOptions: props.hideRowsPerPage ? [] : !props.showAll ? props.pageSizeOptions || [10, 15, 20, 30] : [],
        onRowsPerPageChange: !props.showAll && handleOnPageSizeChange,
        onPageChange: !props.showAll && handleOnPageChange,
        component: "div",
        page: !props.showAll && tableState.rows.length ? tableState.pageIndex : 0,
        rowsPerPage: !props.showAll && tableState.pageSize,
        showFirstButton: true,
        showLastButton: true,
        getItemAriaLabel: (type: "first" | "last" | "next" | "previous") => type,
        count: tableState.total,
        sx: { borderBottom: "none" },
    } as TablePaginationBaseProps;
    const customToolbar = useCallback(
        (toolbarProps: any) => {
            return (
                <CustomGridToolbar
                    {...toolbarProps}
                    showAll={props.showAll}
                    tableTitle={props.title}
                    totalRecords={tableState.total}
                    options={{ ...props.toolbarOptions }}
                    quickSearch={props.showQuickSearch}
                    showPagination={props.pagination}
                />
            );
        },
        [props.pagination, props.showAll, props.showQuickSearch, props.title, props.toolbarOptions, tableState.total]
    );
    const customColumnMenu = useCallback((columnMenuProps: any) => {
        return <ColumnMenu {...columnMenuProps} setPanelAnchorEl={setPanelAnchorEl} />;
    }, []);

    const footer = useCallback(() => <Footer legendItems={legend} />, [legend]);
    const getRowClassName = useCallback(
        (params: GridRowClassNameParams) => {
            if (!legend) {
                return "";
            }
            const legendItem = legend.find((legendItem) => legendItem.applyClass(params));
            if (!legendItem) {
                return "";
            }
            const rowColor = legendItem.color;
            return `entityTable__row--${rowColor}`;
        },
        [legend]
    );
    const onResize = useCallback(({ colDef }: GridColumnResizeParams) => {
        const copy = { ...uiSettings.current };
        const getColumns = () => copy?.columns || [];
        const columns = [...getColumns()];
        const columnIdx = columns.findIndex((c: { field: string }) => c.field === colDef.field);
        if (columnIdx !== -1) {
            columns[columnIdx] = { field: colDef.field, width: colDef.width };
        } else {
            columns.push({ field: colDef.field, width: colDef.width });
        }
        uiSettings.current = { ...copy, columns };
    }, []);
    const onColumnOrderChange = useCallback(() => {
        const copy = { ...uiSettings.current };
        const visibleColumns = gridApiRef.current?.getVisibleColumns();
        if (visibleColumns?.length) {
            const orderedFields = visibleColumns.map((c: { field: string }) => c.field);
            uiSettings.current = { ...copy, columnOrder: orderedFields };
        }
    }, [gridApiRef]);
    const clearPreferences = useCallback(() => {
        uiSettings.current = null;
        setTableState((prev) => {
            return {
                ...prev,
                columnVisibilityModel,
                pinnedColumns: props.pinnedColumns || ({} as GridPinnedColumns),
                pageSize: props.pageSize || 10,
            };
        });
        userPreferencesService.clearPreferences({ preference: "entityTables", key: stateKey });
        setTimeout(() => {
            const preferencesCopy = cloneDeep(userPreferences);
            if (stateKey in preferencesCopy.entityTables) {
                delete preferencesCopy.entityTables[stateKey];
            }
            setUserPreferences(preferencesCopy);
        }, 200);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnVisibilityModel, props.pageSize, props.pinnedColumns, setUserPreferences, stateKey, uiSettings.current]);

    if (hideEmpty && !tableState.total && !tableState.filterModel.items.length) {
        return null;
    }

    const table = (
        <EntityTableProvider value={{ setDateFilterPopper, dateFilterPopper, apiRef: gridApiRef, showAlert }}>
            <DateFilterModal />
            <DataGridPremium
                slots={{
                    columnMenu: customColumnMenu,
                    noRowsOverlay: CustomNoRowsOverlay,
                    toolbar: !props.hideToolbar ? customToolbar : null,
                    footer: footer,
                }}
                slotProps={{
                    noRowsOverlay: { noRecordsImage: props.noRecordsImage, noRecordsMessage: props.noRecordsMessage },
                    toolbar: {
                        rowMode,
                        tableState,
                        setTableState,
                        onSave: props.onEditRecordSave,
                        paginationProps,
                        anchorEl: panelAnchorEl,
                        setAnchorEl: setPanelAnchorEl,
                        clearPreferences,
                    },
                    row: { onFocus: handleRowFocus },
                    panel: {
                        anchorEl: panelAnchorEl ? panelAnchorEl : gridApiRef.current?.rootElementRef?.current?.querySelector(".MuiDataGrid-columnHeaders"),
                        placement: "bottom-start",
                    },
                }}
                getRowClassName={getRowClassName}
                sx={{
                    maxHeight: props.gridMaxHeight,
                }}
                {...props}
                disableColumnMenu={props.disableColumnMenu || isPublicOnlyMode}
                disableColumnFilter={props.disableColumnFilter || isPublicOnlyMode}
                className={!props.gridHeight ? props.className : undefined}
                apiRef={gridApiRef}
                autoHeight={props.gridHeight || props.gridMaxHeight ? false : true}
                rows={tableState.rows}
                editMode={props.editMode ? props.editMode : "row"}
                rowModesModel={tableState.rowModesModel}
                onRowModesModelChange={handleRowsModeChanged}
                onRowEditStart={handleRowEditStart}
                onRowEditStop={handleRowEditStop}
                onRowEditCommit={handleRowEditCommit}
                columns={columns}
                loading={tableState.isLoading}
                pagination={props.pagination === false || props.showAll ? false : true}
                paginationMode={"server"}
                rowCount={tableState.total}
                disableRowGrouping={true} // disabled as it doesn't work now
                sortingMode={"server"}
                sortModel={tableState.sortModel}
                onSortModelChange={handleOnSortModelChange}
                filterMode={"server"}
                onFilterModelChange={handleFilterModelChanged}
                filterModel={tableState.filterModel}
                hideFooter={props.legend ? false : true}
                hideFooterRowCount={props.pagination === false ? true : false}
                hideFooterSelectedRowCount={props.pagination === false ? true : false}
                hideFooterPagination={true}
                onRowClick={props.onRowClick ? props.onRowClick : handleRowClick}
                onCellClick={props.onCellClick ? props.onCellClick : handleCellClick}
                keepNonExistentRowsSelected // clear selection with 'rowSelectionModel'
                columnVisibilityModel={tableState.columnVisibilityModel}
                onColumnVisibilityModelChange={onColumnVisibilityModelChange}
                onColumnWidthChange={onResize}
                onColumnOrderChange={onColumnOrderChange}
                pinnedColumns={tableState.pinnedColumns}
                onPinnedColumnsChange={(pinnedColumns) => setTableState((prev) => ({ ...prev, pinnedColumns }))}
            />
        </EntityTableProvider>
    );

    if (!props.gridHeight) {
        return table;
    }
    return (
        <div className={props.className} style={{ height: props.gridHeight }}>
            {table}
        </div>
    );
});

export type ICustomTogglePanelCol = CustomTogglePanelColDefinition | undefined;

export type CustomTogglePanelColDefinition = {
    headerName: string;
    renderCell: (params: GridRenderCellParams<any, any, any>) => ReactNode;
};

export default EntityTable;
